/**
 * Geometry Processor Service
 *
 * Core service for geometry processing operations including dissolve,
 * simplify, and geometry property calculations using Turf.js.
 *
 * **Validates: Requirements 50.1, 50.2, 50.4, 50.5, 50.6, 51.1, 51.2, 51.4, 51.5, 51.6,
 *              52.1, 52.2, 52.3, 52.4, 52.5, 52.6, 52.7**
 */

import union from '@turf/union';
import simplify from '@turf/simplify';
import area from '@turf/area';
import length from '@turf/length';
import centroid from '@turf/centroid';
import {
  featureCollection,
  polygon as turfPolygon,
  lineString as turfLineString,
} from '@turf/helpers';
import type {
  Feature,
  FeatureCollection,
  Geometry,
  Polygon,
  MultiPolygon,
  LineString,
  MultiLineString,
  Point,
  GeoJsonProperties,
} from 'geojson';
import { validateGeometry, type ValidationResult } from '../utils/validateGeometry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AggregationOperation = 'sum' | 'mean' | 'min' | 'max' | 'count';

export type GeometricProperty = 'area' | 'perimeter' | 'length' | 'x-coordinate' | 'y-coordinate';

export type LengthUnit = 'meters' | 'kilometers' | 'feet' | 'miles';
export type AreaUnit = 'square-meters' | 'square-kilometers' | 'hectares' | 'acres';

export interface FieldAggregation {
  field: string;
  operation: AggregationOperation;
}

export interface DissolveOptions {
  dissolveField: string;
  aggregations: FieldAggregation[];
}

export interface DissolveResult {
  success: boolean;
  features: Feature[];
  message?: string;
  validationErrors?: string[];
}

export interface SimplifyOptions {
  tolerance: number;
  highQuality?: boolean;
}

export interface SimplifyResult {
  success: boolean;
  features: Feature[];
  message?: string;
  validationErrors?: string[];
}

export interface CalculateOptions {
  properties: GeometricProperty[];
  lengthUnit: LengthUnit;
  areaUnit: AreaUnit;
}

export interface CalculateResult {
  success: boolean;
  features: Feature[];
  message?: string;
}

export interface GeometryProcessorFeature {
  id: string;
  geometry: Geometry;
  properties: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Unit conversion constants
// ---------------------------------------------------------------------------

const AREA_CONVERSIONS: Record<AreaUnit, number> = {
  'square-meters': 1,
  'square-kilometers': 1e-6,
  hectares: 1e-4,
  acres: 0.000247105,
};

const LENGTH_CONVERSIONS: Record<LengthUnit, number> = {
  meters: 1000, // turf/length returns km by default
  kilometers: 1,
  feet: 3280.84,
  miles: 0.621371,
};

// ---------------------------------------------------------------------------
// Geometry Processor
// ---------------------------------------------------------------------------

export class GeometryProcessor {
  /**
   * Dissolves features by merging those with matching attribute values.
   * Removes internal boundaries and supports attribute aggregation.
   *
   * Requirements: 50.2, 50.4, 50.5, 50.6
   */
  dissolveFeatures(features: GeometryProcessorFeature[], options: DissolveOptions): DissolveResult {
    const { dissolveField, aggregations } = options;

    if (!features || features.length === 0) {
      return {
        success: false,
        features: [],
        message: 'No features provided for dissolve operation',
      };
    }

    if (!dissolveField) {
      return { success: false, features: [], message: 'Dissolve field is required' };
    }

    // Verify all features have the dissolve field
    const missingField = features.find((f) => f.properties[dissolveField] === undefined);
    if (missingField) {
      return {
        success: false,
        features: [],
        message: `Some features are missing the dissolve field "${dissolveField}"`,
      };
    }

    try {
      // Group features by dissolve field value
      const groups = new Map<string, GeometryProcessorFeature[]>();
      for (const feature of features) {
        const key = String(feature.properties[dissolveField]);
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key)!.push(feature);
      }

      const resultFeatures: Feature[] = [];
      const validationErrors: string[] = [];

      for (const [groupValue, groupFeatures] of groups) {
        // Union all geometries in the group to remove internal boundaries
        const mergedGeometry = this.unionGeometries(groupFeatures);

        if (!mergedGeometry) {
          validationErrors.push(`Failed to merge geometries for ${dissolveField}="${groupValue}"`);
          continue;
        }

        // Calculate aggregated attributes
        const aggregatedProps = this.calculateAggregations(groupFeatures, aggregations);

        // Build result feature properties
        const resultProps: GeoJsonProperties = {
          [dissolveField]: groupFeatures[0].properties[dissolveField],
          ...aggregatedProps,
          _featureCount: groupFeatures.length,
        };

        const resultFeature: Feature = {
          type: 'Feature',
          geometry: mergedGeometry,
          properties: resultProps,
        };

        // Validate topology of dissolved geometry
        const validation = validateGeometry(mergedGeometry as any);
        if (!validation.valid) {
          validationErrors.push(
            `Dissolved geometry for ${dissolveField}="${groupValue}" has topology issues: ${validation.errors.join(', ')}`
          );
        }

        resultFeatures.push(resultFeature);
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Dissolved ${features.length} features into ${resultFeatures.length} groups by "${dissolveField}"`,
        validationErrors: validationErrors.length > 0 ? validationErrors : undefined,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Dissolve operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Simplifies feature geometries using the Douglas-Peucker algorithm.
   * Preserves attributes during simplification.
   *
   * Requirements: 51.1, 51.2, 51.4, 51.5, 51.6
   */
  simplifyGeometries(
    features: GeometryProcessorFeature[],
    options: SimplifyOptions
  ): SimplifyResult {
    const { tolerance, highQuality = true } = options;

    if (!features || features.length === 0) {
      return { success: false, features: [], message: 'No features provided for simplification' };
    }

    if (tolerance <= 0) {
      return { success: false, features: [], message: 'Tolerance must be a positive number' };
    }

    try {
      const resultFeatures: Feature[] = [];
      const validationErrors: string[] = [];

      for (const feature of features) {
        const geojsonFeature: Feature = {
          type: 'Feature',
          geometry: feature.geometry,
          properties: { ...feature.properties },
        };

        // Apply Douglas-Peucker simplification via Turf.js
        const simplified = simplify(geojsonFeature, {
          tolerance,
          highQuality,
        });

        if (simplified) {
          // Preserve all original attributes (Requirement 51.4)
          simplified.properties = { ...feature.properties };

          // Validate simplified geometry topology (Requirement 51.6)
          const validation = validateGeometry(simplified.geometry as any);
          if (!validation.valid) {
            validationErrors.push(
              `Feature "${feature.id}" simplified geometry has topology issues: ${validation.errors.join(', ')}`
            );
          }

          resultFeatures.push(simplified);
        }
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Simplified ${resultFeatures.length} feature(s) with tolerance ${tolerance}`,
        validationErrors: validationErrors.length > 0 ? validationErrors : undefined,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Simplify operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Calculates geometric properties for features.
   * Supports area, perimeter, length, and X/Y coordinates.
   *
   * Requirements: 52.1, 52.2, 52.3, 52.4, 52.5, 52.6
   */
  calculateGeometryProperties(
    features: GeometryProcessorFeature[],
    options: CalculateOptions
  ): CalculateResult {
    const { properties, lengthUnit, areaUnit } = options;

    if (!features || features.length === 0) {
      return { success: false, features: [], message: 'No features provided for calculation' };
    }

    if (!properties || properties.length === 0) {
      return { success: false, features: [], message: 'No properties selected for calculation' };
    }

    try {
      const resultFeatures: Feature[] = [];

      for (const feature of features) {
        const calculatedValues: Record<string, number> = {};

        for (const prop of properties) {
          const value = this.calculateProperty(feature, prop, lengthUnit, areaUnit);
          if (value !== null) {
            calculatedValues[this.getFieldName(prop, lengthUnit, areaUnit)] = value;
          }
        }

        // Add calculated values as new attribute fields (Requirement 52.5)
        const resultFeature: Feature = {
          type: 'Feature',
          geometry: feature.geometry,
          properties: {
            ...feature.properties,
            ...calculatedValues,
          },
        };

        resultFeatures.push(resultFeature);
      }

      return {
        success: true,
        features: resultFeatures,
        message: `Calculated ${properties.length} property(ies) for ${resultFeatures.length} feature(s)`,
      };
    } catch (error) {
      return {
        success: false,
        features: [],
        message: `Calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Updates calculated fields when geometry changes.
   * Recalculates specified properties for the given features.
   *
   * Requirement: 52.7
   */
  updateCalculatedFields(
    features: GeometryProcessorFeature[],
    properties: GeometricProperty[],
    lengthUnit: LengthUnit,
    areaUnit: AreaUnit
  ): CalculateResult {
    return this.calculateGeometryProperties(features, {
      properties,
      lengthUnit,
      areaUnit,
    });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Unions multiple feature geometries into a single geometry.
   * Removes internal boundaries between merged features.
   */
  private unionGeometries(features: GeometryProcessorFeature[]): Geometry | null {
    if (features.length === 0) return null;
    if (features.length === 1) return features[0].geometry;

    const polygonFeatures = features.filter(
      (f) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
    );

    if (polygonFeatures.length === 0) {
      // For non-polygon features, just return the first geometry
      return features[0].geometry;
    }

    try {
      let accumulated: Feature<Polygon | MultiPolygon> = {
        type: 'Feature',
        geometry: polygonFeatures[0].geometry as Polygon | MultiPolygon,
        properties: {},
      };

      for (let i = 1; i < polygonFeatures.length; i++) {
        const nextFeature: Feature<Polygon | MultiPolygon> = {
          type: 'Feature',
          geometry: polygonFeatures[i].geometry as Polygon | MultiPolygon,
          properties: {},
        };

        const result = union(featureCollection([accumulated, nextFeature]));
        if (result) {
          accumulated = result as Feature<Polygon | MultiPolygon>;
        }
      }

      return accumulated.geometry;
    } catch {
      return null;
    }
  }

  /**
   * Calculates aggregated values for numeric fields.
   */
  private calculateAggregations(
    features: GeometryProcessorFeature[],
    aggregations: FieldAggregation[]
  ): Record<string, number> {
    const result: Record<string, number> = {};

    for (const agg of aggregations) {
      const values = features
        .map((f) => f.properties[agg.field])
        .filter((v) => typeof v === 'number' && !isNaN(v)) as number[];

      if (values.length === 0) continue;

      switch (agg.operation) {
        case 'sum':
          result[`${agg.field}_sum`] = values.reduce((a, b) => a + b, 0);
          break;
        case 'mean':
          result[`${agg.field}_mean`] = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case 'min':
          result[`${agg.field}_min`] = Math.min(...values);
          break;
        case 'max':
          result[`${agg.field}_max`] = Math.max(...values);
          break;
        case 'count':
          result[`${agg.field}_count`] = values.length;
          break;
      }
    }

    return result;
  }

  /**
   * Calculates a single geometric property for a feature.
   */
  private calculateProperty(
    feature: GeometryProcessorFeature,
    property: GeometricProperty,
    lengthUnit: LengthUnit,
    areaUnit: AreaUnit
  ): number | null {
    const geojsonFeature: Feature = {
      type: 'Feature',
      geometry: feature.geometry,
      properties: feature.properties,
    };

    switch (property) {
      case 'area':
        return this.calculateArea(geojsonFeature, areaUnit);
      case 'perimeter':
        return this.calculatePerimeter(geojsonFeature, lengthUnit);
      case 'length':
        return this.calculateLength(geojsonFeature, lengthUnit);
      case 'x-coordinate':
        return this.calculateXCoordinate(geojsonFeature);
      case 'y-coordinate':
        return this.calculateYCoordinate(geojsonFeature);
      default:
        return null;
    }
  }

  /**
   * Calculates area for polygon features using Turf.js.
   * Returns area in the specified unit.
   *
   * Requirement: 52.2
   */
  private calculateArea(feature: Feature, unit: AreaUnit): number | null {
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
      return null;
    }

    // turf/area returns square meters
    const areaInSqMeters = area(feature);
    return areaInSqMeters * AREA_CONVERSIONS[unit];
  }

  /**
   * Calculates perimeter for polygon features.
   * Converts polygon boundary to linestring and measures length.
   *
   * Requirement: 52.2
   */
  private calculatePerimeter(feature: Feature, unit: LengthUnit): number | null {
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
      return null;
    }

    try {
      const polygons =
        feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates]
          : feature.geometry.coordinates;
      let totalLengthKm = 0;

      for (const polygon of polygons) {
        for (const ring of polygon) {
          const ringFeature: Feature<LineString> = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: ring },
            properties: {},
          };
          totalLengthKm += length(ringFeature);
        }
      }

      return totalLengthKm * LENGTH_CONVERSIONS[unit];
    } catch {
      return null;
    }
  }

  /**
   * Calculates length for line features.
   *
   * Requirement: 52.3
   */
  private calculateLength(feature: Feature, unit: LengthUnit): number | null {
    if (feature.geometry.type !== 'LineString' && feature.geometry.type !== 'MultiLineString') {
      return null;
    }

    // turf/length returns km by default
    const lengthKm = length(feature);
    return lengthKm * LENGTH_CONVERSIONS[unit];
  }

  /**
   * Calculates X coordinate for point features, or centroid X for polygons/lines.
   *
   * Requirement: 52.4
   */
  private calculateXCoordinate(feature: Feature): number | null {
    if (feature.geometry.type === 'Point') {
      return feature.geometry.coordinates[0];
    }

    // For non-point features, use centroid
    try {
      const center = centroid(feature);
      return center.geometry.coordinates[0];
    } catch {
      return null;
    }
  }

  /**
   * Calculates Y coordinate for point features, or centroid Y for polygons/lines.
   *
   * Requirement: 52.4
   */
  private calculateYCoordinate(feature: Feature): number | null {
    if (feature.geometry.type === 'Point') {
      return feature.geometry.coordinates[1];
    }

    // For non-point features, use centroid
    try {
      const center = centroid(feature);
      return center.geometry.coordinates[1];
    } catch {
      return null;
    }
  }

  /**
   * Generates a field name for a calculated property.
   */
  private getFieldName(
    property: GeometricProperty,
    lengthUnit: LengthUnit,
    areaUnit: AreaUnit
  ): string {
    switch (property) {
      case 'area':
        return `calc_area_${areaUnit.replace('-', '_')}`;
      case 'perimeter':
        return `calc_perimeter_${lengthUnit}`;
      case 'length':
        return `calc_length_${lengthUnit}`;
      case 'x-coordinate':
        return 'calc_x';
      case 'y-coordinate':
        return 'calc_y';
      default:
        return `calc_${property}`;
    }
  }
}

// Export singleton instance
export const geometryProcessor = new GeometryProcessor();
export default geometryProcessor;
